import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { api } from '../services/authService';

const triggerTypes = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'delay', label: 'Time Delay' },
  { value: 'funnel_change', label: 'Funnel Step Change' },
  { value: 'event', label: 'Event Based' },
  { value: 'condition', label: 'Condition Met' },
  { value: 'manual', label: 'Manual Trigger' },
];

const delayUnits = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
];

const ConnectionModal = ({
  open,
  onClose,
  editingConnection,
  selectedTargetStep,
  setSelectedTargetStep,
  triggerType,
  setTriggerType,
  delayDuration,
  setDelayDuration,
  delayUnit,
  setDelayUnit,
  funnelStep,
  setFunnelStep,
  eventType,
  setEventType,
  conditionLabel,
  setConditionLabel,
  conditionConfig,
  setConditionConfig,
  priority,
  setPriority,
  availableSteps,
  onCreateConnection,
  isValidConnection,
  journey,
  step,
}) => {
  const [error, setError] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  
  // New state for condition configuration
  const [conditionTypes, setConditionTypes] = useState([]);
  const [fieldSources, setFieldSources] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
  const [conditionType, setConditionType] = useState('');
  const [fieldSource, setFieldSource] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [fieldValue, setFieldValue] = useState('');

  // Log initial props and state
  console.log('ConnectionModal Props:', {
    journey,
    journeyFunnelId: journey?.funnel,
    funnelData: journey?.funnel_data,
    triggerType,
    isOpen: open
  });

  // Get funnel steps from journey object
  const funnelSteps = journey?.funnel_data?.steps || [];

  // Fetch event types when modal opens
  useEffect(() => {
    const fetchEventTypes = async () => {
      if (open) {
        setLoadingEventTypes(true);
        try {
          const response = await api.get('/acs/journey-step-connections/event_types/');
          console.log('ConnectionModal - Fetched event types:', response.data);
          setEventTypes(response.data);
        } catch (err) {
          console.error('ConnectionModal - Error fetching event types:', err);
          setError('Failed to load event types');
        } finally {
          setLoadingEventTypes(false);
        }
      }
    };

    fetchEventTypes();
  }, [open]);

  // Fetch condition types and field sources when modal opens
  useEffect(() => {
    const fetchConditionData = async () => {
      if (open) {
        try {
          // Fetch condition types
          const conditionTypesRes = await api.get('/acs/journey-step-connections/condition_types/');
          setConditionTypes(conditionTypesRes.data);

          // Fetch field sources
          const fieldSourcesRes = await api.get('/acs/journey-step-connections/field_sources/');
          setFieldSources(fieldSourcesRes.data);
        } catch (err) {
          console.error('ConnectionModal - Error fetching condition data:', err);
          setError('Failed to load condition configuration');
        }
      }
    };

    fetchConditionData();
  }, [open]);

  // Fetch field options when field source changes
  useEffect(() => {
    const fetchFieldOptions = async () => {
      if (fieldSource) {
        setLoadingFieldOptions(true);
        try {
          const response = await api.get(`/acs/journey-step-connections/field_options/?source=${fieldSource}`);
          setFieldOptions(response.data);
        } catch (err) {
          console.error('ConnectionModal - Error fetching field options:', err);
          setError('Failed to load field options');
        } finally {
          setLoadingFieldOptions(false);
        }
      } else {
        setFieldOptions([]);
      }
    };

    fetchFieldOptions();
  }, [fieldSource]);

  // Update condition label when condition configuration changes
  useEffect(() => {
    if (triggerType === 'condition') {
      const conditionTypeLabel = conditionTypes.find(ct => ct.value === conditionType)?.label || '';
      const fieldSourceLabel = fieldSources.find(fs => fs.value === fieldSource)?.label || '';
      const fieldNameLabel = fieldOptions.find(fo => fo.value === fieldName)?.label || fieldName;
      
      let label = '';
      if (conditionType && fieldSource && fieldName) {
        label = `${conditionTypeLabel} ${fieldNameLabel}`;
        if (conditionType !== 'field_is_empty' && conditionType !== 'field_is_not_empty' && fieldValue) {
          label += ` ${fieldValue}`;
        }
      }
      setConditionLabel(label);
    }
  }, [conditionType, fieldSource, fieldName, fieldValue, conditionTypes, fieldSources, fieldOptions]);

  // Log when funnel steps are accessed
  useEffect(() => {
    if (triggerType === 'funnel_change') {
      console.log('ConnectionModal - Funnel Steps from Journey:', {
        funnelId: journey?.funnel,
        funnelData: journey?.funnel_data,
        stepsCount: funnelSteps.length,
        steps: funnelSteps
      });
    }
  }, [triggerType, journey, funnelSteps]);

  // Log connection data when creating/editing
  useEffect(() => {
    if (open) {
      console.log('ConnectionModal - Connection Data:', {
        editingConnection,
        fromStep: step?.id,
        toStep: selectedTargetStep,
        triggerType,
        funnelStep,
        priority
      });
    }
  }, [open, editingConnection, step, selectedTargetStep, triggerType, funnelStep, priority]);

  const handleCreateConnection = () => {
    if (!selectedTargetStep) return;

    const config = {
      trigger_type: triggerType,
      priority: priority,
    };

    // Add trigger-specific data
    switch (triggerType) {
      case 'delay':
        config.delay_duration = parseInt(delayDuration);
        config.delay_unit = delayUnit;
        config.condition_label = `After ${delayDuration} ${delayUnit}`;
        break;
      case 'funnel_change':
        config.funnel_step = parseInt(funnelStep);
        config.condition_label = `On funnel step change`;
        break;
      case 'event':
        config.event_type = eventType;
        config.condition_label = `On ${eventTypes.find(et => et.value === eventType)?.label || eventType} event`;
        break;
      case 'condition':
        config.condition_type = conditionType;
        config.field_source = fieldSource;
        config.field_name = fieldName;
        config.field_value = fieldValue;
        config.condition_label = conditionLabel;
        break;
      case 'manual':
        config.condition_label = 'Manual trigger';
        break;
      default:
        config.condition_label = 'Immediate';
    }

    console.log('ConnectionModal - Creating/Updating Connection:', {
      config,
      isEditing: !!editingConnection,
      fromStep: step?.id,
      toStep: selectedTargetStep
    });

    onCreateConnection(config);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {editingConnection ? 'Edit Connection' : 'Create Connection'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Target Step</InputLabel>
              <Select
                value={selectedTargetStep}
                onChange={(e) => setSelectedTargetStep(e.target.value)}
                label="Target Step"
              >
                {availableSteps.map((targetStep) => (
                  <MenuItem key={targetStep.id} value={targetStep.id}>
                    {targetStep.name} ({targetStep.step_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Trigger Type</InputLabel>
              <Select
                value={triggerType}
                onChange={(e) => setTriggerType(e.target.value)}
                label="Trigger Type"
              >
                {triggerTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Delay Configuration */}
          {triggerType === 'delay' && (
            <>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  type="number"
                  label="Delay Duration"
                  value={delayDuration}
                  onChange={(e) => setDelayDuration(e.target.value)}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={delayUnit}
                    onChange={(e) => setDelayUnit(e.target.value)}
                    label="Unit"
                  >
                    {delayUnits.map((unit) => (
                      <MenuItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Funnel Step Configuration */}
          {triggerType === 'funnel_change' && (
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Funnel Step</InputLabel>
                {!journey?.funnel_data ? (
                  <Box sx={{ mt: 1 }}>
                    <Alert severity="error" sx={{ py: 0 }}>
                      No funnel data available
                    </Alert>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Funnel ID: {journey?.funnel}
                    </Typography>
                  </Box>
                ) : funnelSteps.length === 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Alert severity="info" sx={{ py: 0 }}>
                      No funnel steps available
                    </Alert>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Funnel ID: {journey?.funnel}
                    </Typography>
                  </Box>
                ) : (
                  <Select
                    value={funnelStep}
                    onChange={(e) => setFunnelStep(e.target.value)}
                    label="Funnel Step"
                  >
                    {funnelSteps.map((step) => (
                      <MenuItem key={step.id} value={step.id}>
                        {step.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </Grid>
          )}

          {/* Event Configuration */}
          {triggerType === 'event' && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                {loadingEventTypes ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="textSecondary">
                      Loading event types...
                    </Typography>
                  </Box>
                ) : error ? (
                  <Box sx={{ mt: 1 }}>
                    <Alert severity="error" sx={{ py: 0 }}>
                      {error}
                    </Alert>
                  </Box>
                ) : (
                  <Select
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    label="Event Type"
                  >
                    {eventTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </Grid>
          )}

          {/* Condition Configuration */}
          {triggerType === 'condition' && (
            <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Condition Type</InputLabel>
                  <Select
                    value={conditionType}
                    onChange={(e) => setConditionType(e.target.value)}
                    label="Condition Type"
                  >
                    {conditionTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Field Source</InputLabel>
                  <Select
                    value={fieldSource}
                    onChange={(e) => {
                      setFieldSource(e.target.value);
                      setFieldName(''); // Reset field name when source changes
                    }}
                    label="Field Source"
                  >
                    {fieldSources.map((source) => (
                      <MenuItem key={source.value} value={source.value}>
                        {source.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Field Name</InputLabel>
                  {loadingFieldOptions ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="textSecondary">
                        Loading fields...
                      </Typography>
                    </Box>
                  ) : (
                    <Select
                      value={fieldName}
                      onChange={(e) => setFieldName(e.target.value)}
                      label="Field Name"
                      disabled={!fieldSource}
                    >
                      {fieldOptions.map((field) => (
                        <MenuItem key={field.value} value={field.value}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid>

              {conditionType && 
               conditionType !== 'field_is_empty' && 
               conditionType !== 'field_is_not_empty' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Field Value"
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    placeholder="Enter value to compare against"
                  />
                </Grid>
              )}
            </>
          )}

          {/* Priority */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value))}
              InputProps={{ inputProps: { min: 1 } }}
              helperText="Lower number means higher priority"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateConnection}
          variant="contained"
          disabled={!selectedTargetStep || !isValidConnection()}
        >
          {editingConnection ? 'Update Connection' : 'Create Connection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectionModal; 